import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import ResultsSection from './ResultsSection.jsx';

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

const parsePositiveDay = (value) => {
  const normalized = normalizeDayValue(value);
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};


// Seulement pour coupe du monde 2025
//supprimer/remplacer plus tard par une vraie config au besoin
const C42025_DAY_LABELS = {
  4: '16es finale',
  5: '8es de finale',
  6: 'Quarts de finale',
  7: 'Demi-finales',
  8: 'Match pour la 3e place',
  9: 'Finale',
};

const getDayDisplayLabel = (dayValue, isOptaC42025Competition = false) => {
  const numericDay = parsePositiveDay(dayValue);

  if (isOptaC42025Competition && C42025_DAY_LABELS[numericDay]) {
    return C42025_DAY_LABELS[numericDay];
  }

  return `Journée ${dayValue}`;
};

export default function ResultsWidget({
  discipline = 'rugby',
  competitionIds = [],
  competitionInfos = [],
  day = null,
  onNavigate,
  onDayChange,
  onGroupChange,
  groupKey = '',
  widgetConfigurationId=null,
  title,
  showCompetitionInfos=true,
}) {
  const [sectionMeta, setSectionMeta] = useState({
    selectedDay: null,
    nbDays: null,
  });

  console.log('ResultsWidget render showCompetitionInfos=', showCompetitionInfos, 'competitionInfos=', competitionInfos);

  const isMultiCompetition = competitionIds.length > 1;
  const competitionMeta = competitionInfos?.[0] || {};

  const groupOptions = useMemo(() => {
  const byKey = new Map();

  (competitionInfos || []).forEach((competition) => {
    if (
      competition?.rankingMode !== 'groups' ||
      !Array.isArray(competition?.groups)
    ) {
      return;
    }

    competition.groups.forEach((group) => {
      if (!group?.key || byKey.has(group.key)) return;

      byKey.set(group.key, {
        key: group.key,
        label: group.label || `Groupe ${group.key}`,
        order: Number(group.order) || 999,
      });
    });
  });

  return Array.from(byKey.values()).sort((a, b) => {
    const orderDiff = a.order - b.order;
    if (orderDiff !== 0) return orderDiff;
    return String(a.key).localeCompare(String(b.key));
  });
}, [competitionInfos]);

const hasGroupFilter = !isMultiCompetition && groupOptions.length > 0;

const isCurrentGroupValid =
  groupKey &&
  groupOptions.some((group) => String(group.key) === String(groupKey));

const selectedGroupKey = hasGroupFilter && isCurrentGroupValid
  ? groupKey
  : '';

  const isOptaC42025Competition = useMemo(() => {
    return (competitionInfos || []).some(
      (competition) =>
        String(competition?.thirdPartyId || '').toLowerCase() === 'opta' &&
        String(competition?.thirdPartyEntityId || '') === 'c4-2025'
    );
  }, [competitionInfos]);

  const resolvedSeason = competitionMeta?.season || null;
  const rounds = Array.isArray(competitionMeta?.rounds) ? competitionMeta.rounds : [];

  const roundChoices = useMemo(() => {
    const seen = new Set();

    return rounds.filter((round) => {
      const key = String(round?.key || round?.label || '').trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rounds]);

  const hasMultipleRoundChoices = roundChoices.length > 1;

  const displayedDay = useMemo(() => {
    return normalizeDayValue(sectionMeta.selectedDay) ?? normalizeDayValue(day);
  }, [sectionMeta.selectedDay, day]);

  const displayedNumericDay = useMemo(() => {
    return parsePositiveDay(displayedDay);
  }, [displayedDay]);

  const maxDay = useMemo(() => {
    return (
      parsePositiveDay(sectionMeta.nbDays) ??
      parsePositiveDay(competitionMeta?.nbDays)
    );
  }, [sectionMeta.nbDays, competitionMeta]);

  const dayOptions = useMemo(() => {
    if (!maxDay || maxDay < 1) return [];

    return Array.from({ length: maxDay }, (_, index) => {
      const dayValue = index + 1;

      return {
        value: String(dayValue),
        label: getDayDisplayLabel(dayValue, isOptaC42025Competition),
      };
    });
  }, [maxDay, isOptaC42025Competition]);

  const currentRound = useMemo(() => {
    if (!displayedDay) return null;

    return (
      rounds.find(
        (round) =>
          Array.isArray(round?.days) &&
          round.days.map(normalizeDayValue).includes(displayedDay)
      ) || null
    );
  }, [rounds, displayedDay]);

  const selectedRoundKey = useMemo(() => {
    if (currentRound?.key) return currentRound.key;
    if (displayedNumericDay) return 'regular';
    return '';
  }, [currentRound, displayedNumericDay]);

  const isNumericRound =
    currentRound?.type === 'numeric' ||
    (!currentRound && displayedNumericDay != null);

  const canGoPrevious = Boolean(displayedNumericDay && displayedNumericDay > 1);
  const canGoNext = Boolean(
    displayedNumericDay && maxDay && displayedNumericDay < maxDay
  );
  const canShowDayNavigation =
    !isMultiCompetition && Boolean(displayedDay) && isNumericRound;

  const canSelectDay =
    Boolean(onDayChange) &&
    displayedNumericDay != null &&
    dayOptions.length > 1;

  const handlePreviousDay = () => {
    if (!canGoPrevious || !onDayChange || displayedNumericDay == null) return;
    onDayChange(String(displayedNumericDay - 1));
  };

  const handleNextDay = () => {
    if (!canGoNext || !onDayChange || displayedNumericDay == null) return;
    onDayChange(String(displayedNumericDay + 1));
  };

  const handleDaySelectChange = (event) => {
    if (!onDayChange) return;
    onDayChange(String(event.target.value));
  };

  const handleRoundChange = (event) => {
    const nextRoundKey = event.target.value;
    const nextRound = roundChoices.find((round) => round.key === nextRoundKey);

    if (!nextRound || !onDayChange) return;

    if (nextRound.type === 'numeric') {
      const currentDayInRound =
        displayedDay &&
        Array.isArray(nextRound.days) &&
        nextRound.days.map(normalizeDayValue).includes(displayedDay)
          ? displayedDay
          : null;

      const fallbackNumericDay =
        currentDayInRound ||
        normalizeDayValue(competitionMeta?.defaultDay) ||
        normalizeDayValue(nextRound.days?.[0]) ||
        '1';

      onDayChange(fallbackNumericDay);
      return;
    }

    const firstLiteralDay = normalizeDayValue(nextRound.days?.[0]);
    if (firstLiteralDay) {
      onDayChange(firstLiteralDay);
    }
  };

  const handleMetaChange = useCallback((meta) => {
    setSectionMeta((prev) => {
      const nextSelectedDay = normalizeDayValue(meta?.selectedDay);
      const nextNbDays = parsePositiveDay(meta?.nbDays);

      if (
        prev.selectedDay === nextSelectedDay &&
        prev.nbDays === nextNbDays
      ) {
        return prev;
      }

      return {
        selectedDay: nextSelectedDay,
        nbDays: nextNbDays,
      };
    });
  }, []);

  if (!competitionIds.length) return null;

  return (
    <Box className="results-widget">
      <Box className="results-widget__container">
        <Stack className="results-widget__stack" spacing={2}>
          {showCompetitionInfos && competitionInfos.length > 0 ? (
            <Stack className="results-widget__competition-infos" spacing={0.5}>
              {competitionInfos.map((competition) => (
                <Box
                  key={competition.id || competition.name}
                  className="results-widget__competition-info results-widget__header"
                >
                  <Box className="results-widget__title-wrap">
                    <span className="results-widget__title-dot" aria-hidden="true" />

                    <Typography
                      variant="body1"
                      className="results-widget__competition-name"
                    >
                      {competition.name}
                    </Typography>
                  </Box>

                  {competition.season ? (
                    <Typography
                      variant="body2"
                      className="results-widget__competition-season"
                    >
                      {String(competition.season).split('/')[0]}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : title ? (
            <Box className="results-widget__header">
              <Box className="results-widget__title-wrap">
                <span className="results-widget__title-dot" aria-hidden="true" />

                <Typography variant="h5" className="results-widget__title">
                  {title}
                </Typography>
              </Box>
            </Box>
          ) : null}

          <Box className="results-widget__results-panel">
            {isMultiCompetition ? (
              <Typography variant="body2" className="results-widget__multi-label">
                Résultats cumulés - toutes journées confondues
              </Typography>
            ) : (
              <>
              <Box className="results-widget__filters-row">
                {hasGroupFilter ? (
                  <Box className="results-widget__group-nav">
                    <FormControl
                      size="small"
                      variant="standard"
                      className="results-widget__day-select-control"
                    >
                      <Select
                        value={selectedGroupKey}
                        onChange={(event) => onGroupChange?.(event.target.value)}
                        displayEmpty
                        disableUnderline
                        MenuProps={{
                          PaperProps: {
                            className: 'results-widget__menu-paper',
                          },
                        }}
                      >

                         <MenuItem value="" className="results-widget__menu-item">
                          Tous les groupes
                        </MenuItem>

                        {groupOptions.map((group) => (
                          <MenuItem
                            key={group.key}
                            value={group.key}
                            className="results-widget__menu-item"
                          >
                            {group.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}

                {hasMultipleRoundChoices && !canShowDayNavigation ? (
                  <Box className="results-widget__round-select-wrap">
                    <FormControl
                      size="small"
                      className="results-widget__round-control"
                    >
                      <Select
                        value={selectedRoundKey}
                        onChange={handleRoundChange}
                        displayEmpty
                        MenuProps={{
                          PaperProps: {
                            className: 'results-widget__menu-paper',
                          },
                        }}
                      >
                        {roundChoices.map((round) => (
                          <MenuItem
                            key={round.key}
                            value={round.key}
                            className="results-widget__menu-item"
                          >
                            {round.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}

                {canShowDayNavigation ? (
                  <Box className="results-widget__day-nav">
                    <IconButton
                      size="small"
                      onClick={handlePreviousDay}
                      disabled={!canGoPrevious}
                      aria-label="Journée précédente"
                      className="results-widget__day-button"
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>

                    {canSelectDay ? (
                      <FormControl
                        size="small"
                        variant="standard"
                        className="results-widget__day-select-control"
                      >
                        <Select
                          value={String(displayedNumericDay)}
                          onChange={handleDaySelectChange}
                          displayEmpty
                          disableUnderline
                          MenuProps={{
                            PaperProps: {
                              className: 'results-widget__menu-paper',
                            },
                          }}
                        >
                          {dayOptions.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              className="results-widget__menu-item"
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography
                        variant="body2"
                        className="results-widget__day-label"
                      >
                        {getDayDisplayLabel(displayedNumericDay, isOptaC42025Competition)}
                      </Typography>
                    )}

                    <IconButton
                      size="small"
                      onClick={handleNextDay}
                      disabled={!canGoNext}
                      aria-label="Journée suivante"
                      className="results-widget__day-button"
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : !isMultiCompetition &&
                  hasMultipleRoundChoices &&
                  currentRound?.label ? (
                  <Box className="results-widget__phase-nav">
                    <Typography
                      variant="body2"
                      className="results-widget__phase-label"
                    >
                      {currentRound.label}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </>
            )}

            <ResultsSection
              discipline={discipline}
              season={resolvedSeason}
              competitionIds={competitionIds}
              day={day}
              groupKey={selectedGroupKey || null}
              widgetConfigurationId={widgetConfigurationId}
              onNavigate={onNavigate}
              onMetaChange={handleMetaChange}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
